import React from 'react';
import { useNewsletterForm } from '../hooks/useNewsletterForm';

/**
 * Newsletter component props
 */
export interface NewsletterProps {
  /**
   * Display variant: 'section' (full section) or 'inline' (CTA box)
   */
  variant?: 'section' | 'inline';

  /**
   * Custom heading text
   */
  heading?: string;

  /**
   * Custom description text
   */
  description?: string;

  /**
   * Custom disclaimer text
   */
  disclaimer?: string;
}

/**
 * Newsletter subscription component with email form
 *
 * Supports two variants:
 * - 'section': Full section layout (used in HomePage)
 * - 'inline': Inline CTA box (used in EpisodesPage)
 *
 * Usage:
 * ```tsx
 * // Full section (HomePage)
 * <Newsletter variant="section" />
 *
 * // Inline CTA (EpisodesPage)
 * <Newsletter variant="inline" />
 * ```
 */
export const Newsletter: React.FC<NewsletterProps> = ({
  variant = 'section',
  heading = 'Never Miss a Disaster',
  description = 'Subscribe to get new episodes delivered when The Operator deems it convenient (approximately weekly, subject to system availability and whim).',
  disclaimer = 'We don\'t sell your data. We barely store it. Unsubscribe anytime.'
}) => {
  const { email, submitted, loading, error, setEmail, handleSubmit } = useNewsletterForm();

  // Section variant (HomePage style)
  if (variant === 'section') {
    return (
      <section className="newsletter-section">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="section-title">{heading}</h2>
            <p className="text-gray-300 mb-6">{description}</p>

            {submitted ? (
              <div className="text-teal-500 font-medium py-3">
                ✓ Subscribed! Check your email for confirmation.
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>

                {error && (
                  <div className="mt-4 text-red-500 font-medium">
                    {error}
                  </div>
                )}
              </>
            )}

            <p className="text-sm text-gray-500 mt-4">{disclaimer}</p>
          </div>
        </div>
      </section>
    );
  }

  // Inline variant (EpisodesPage style)
  return (
    <div className="bg-gradient-to-br from-red-950/30 to-gray-900 border border-red-500/30 rounded-lg p-8 text-center">
      <h3 className="text-2xl font-bold text-gray-100 mb-3">{heading}</h3>
      <p className="text-gray-300 mb-6 max-w-2xl mx-auto">{description}</p>

      {submitted ? (
        <div className="text-teal-500 font-medium py-3">
          ✓ Subscribed! Check your email for confirmation.
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={loading}
              className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {error && (
            <div className="mt-4 text-red-500 font-medium">
              {error}
            </div>
          )}
        </>
      )}

      <p className="text-xs text-gray-500 mt-3">{disclaimer}</p>
    </div>
  );
};
